import React, { useEffect, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ChevronRight, Clock, ArrowLeft } from 'lucide-react';
import BlogLayout from './BlogLayout';
import BlogSketch from './BlogSketch';
import { BLOG_POSTS, BLOG_CATEGORIES } from '../data/blogData';
import { useBlogLang, useBlogMeta, localizePost } from '../hooks/useBlogLang';
import { useSeo } from '../hooks/useSeo';
import { useReveal } from '../hooks/useScrollFx';
import SignatureSVG from './SignatureSVG';

const SITE = 'https://lempole.com';

function formatDate(iso, lang) {
  const d = new Date(iso);
  return d.toLocaleDateString(lang === 'en' ? 'en-GB' : (lang === 'fr' ? 'fr-FR' : 'it-IT'), { day: '2-digit', month: 'long', year: 'numeric' });
}

export default function BlogPost() {
  const { slug } = useParams();
  const { lang } = useBlogLang();
  const suffix = lang === 'it' ? '' : `?lang=${lang}`;
  const basePost = BLOG_POSTS.find((p) => p.slug === slug);
  const post = useMemo(() => localizePost(basePost, lang), [basePost, lang]);
  const m = useBlogMeta(lang);
  const rootRef = useReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const related = useMemo(() => {
    if (!post) return [];
    return (post.related || [])
      .map((s) => localizePost(BLOG_POSTS.find((p) => p.slug === s), lang))
      .filter(Boolean)
      .slice(0, 3);
  }, [post, lang]);

  const jsonLd = useMemo(() => {
    if (!post) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      '@id': `${SITE}/diario/${post.slug}`,
      headline: post.title,
      description: post.excerpt,
      image: post.cover,
      datePublished: post.date,
      dateModified: post.date,
      inLanguage: lang === 'en' ? 'en' : (lang === 'fr' ? 'fr-FR' : 'it-IT'),
      keywords: (post.keywords || []).join(', '),
      articleSection: BLOG_CATEGORIES[post.category]?.label,
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE}/diario/${post.slug}` },
      author: { '@id': `${SITE}/#raffaello` },
      publisher: { '@id': `${SITE}/#winery` },
      isPartOf: { '@id': `${SITE}/diario#blog` },
    };
  }, [post]);

  const blogTitles = { it: 'Diario di Lempole', en: 'Lempole Diary', fr: 'Journal de Lempole' };
  useSeo({
    title: post ? `${post.title} — ${blogTitles[lang] || blogTitles.it}` : blogTitles[lang] || blogTitles.it,
    description: post?.excerpt,
    canonical: post ? `${SITE}/diario/${post.slug}${suffix}` : `${SITE}/diario`,
    image: post?.cover,
    type: 'article',
    keywords: post?.keywords?.join(', '),
    jsonLd,
  });

  if (!post) return <Navigate to={`/diario${suffix}`} replace />;

  const cat = BLOG_CATEGORIES[post.category];

  return (
    <BlogLayout>
      <div ref={rootRef} className="blog-post-wrap">
        <article className="blog-post">
          <header className="blog-post__head reveal">
            <Link
              to={`/diario${suffix}`}
              className="blog-post__crumb font-kicker"
              data-testid="blog-post-back"
            >
              <ArrowLeft size={14} /> {lang === 'en' ? 'Diary' : (lang === 'fr' ? 'Journal' : 'Diario')}
            </Link>
            <span className="blog-post__cat font-kicker" style={{ color: cat.color }}>
              {cat.label}
            </span>
            <h1 className="blog-post__title font-display">{post.title}</h1>
            <p className="blog-post__excerpt font-display-italic">{post.excerpt}</p>
            <div className="blog-post__meta">
              <time dateTime={post.date}>{formatDate(post.date, lang)}</time>
              <span className="blog-post__dot" aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1">
                <Clock size={12} /> {post.readMinutes} {m.minRead}
              </span>
            </div>
          </header>

          <figure className="blog-post__cover blog-post__cover--sketch reveal" data-testid="blog-post-cover">
            <BlogSketch slug={post.slug} ariaLabel={post.title} />
          </figure>

          <div className="blog-post__body reveal">
            {post.body.map((block, i) => {
              if (block.type === 'h') {
                return (
                  <h2 key={i} className="blog-post__h2 font-display">
                    {block.text}
                  </h2>
                );
              }
              if (block.type === 'img') {
                return (
                  <figure key={i} className="blog-post__figure">
                    <img
                      src={block.src}
                      alt={block.alt || ''}
                      loading="lazy"
                      decoding="async"
                    />
                    {block.caption && (
                      <figcaption className="blog-post__figcaption font-display-italic">
                        {block.caption}
                      </figcaption>
                    )}
                  </figure>
                );
              }
              return (
                <p key={i} className="blog-post__p">
                  {block.text}
                </p>
              );
            })}

            <div className="blog-post__sign reveal">
              <SignatureSVG
                name="Raffaello"
                style={{
                  color: '#3a2418',
                  display: 'block',
                }}
              />
              <Link
                to={`/diario${suffix}`}
                className="blog-post__all-link"
                data-testid="blog-post-all-articles"
              >
                <ArrowLeft size={14} />
                <span>{m.allArticles}</span>
              </Link>
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="blog-related reveal" data-testid="blog-related">
            <h2 className="blog-related__title font-display">
              {m.related.split(' ').slice(0, -1).join(' ')}{' '}
              <em className="font-display-italic">{m.related.split(' ').slice(-1)}</em>
            </h2>
            <ul className="blog-related__grid">
              {related.map((r) => {
                const rcat = BLOG_CATEGORIES[r.category];
                return (
                  <li key={r.slug}>
                    <Link to={`/diario/${r.slug}${suffix}`} className="blog-card" data-testid={`blog-related-${r.slug}`}>
                      <div className="blog-card__cover blog-card__cover--sketch">
                        <BlogSketch slug={r.slug} ariaLabel={r.title} />
                      </div>
                      <div className="blog-card__body">
                        <span className="blog-card__cat font-kicker" style={{ color: rcat.color }}>
                          {rcat.label}
                        </span>
                        <h3 className="blog-card__title font-display">{r.title}</h3>
                        <div className="blog-card__foot">
                          <time dateTime={r.date}>{formatDate(r.date, lang)}</time>
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <div className="blog-post__ctas reveal">
          <Link
            to={`/diario${suffix}`}
            className="hero-cta hero-cta--ink inline-flex items-center gap-3"
            data-testid="blog-post-all-articles"
          >
            <span className="hero-cta__line" aria-hidden="true" />
            <span className="hero-cta__text font-display-italic">{m.allArticles}</span>
            <ChevronRight size={18} className="hero-cta__arrow" />
          </Link>
        </div>
      </div>
    </BlogLayout>
  );
}
